// Complete TeamCalendarPage.js with fixed employee names
import React, { useState, useEffect } from "react";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { collection, query, where, getDocs } from "firebase/firestore";
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Container,
    Paper,
    Box,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Grid,
    Divider,
    IconButton
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    CalendarToday as CalendarTodayIcon,
    Event as EventIcon,
    Group as GroupIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import AttendanceMetrics from "../components/AttendanceMetrics";
import TimeTrackingDashboard from "../components/TimeTrackingDashboard";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`team-tab-${index}`}
            aria-labelledby={`team-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function TeamCalendarPage({ user, orgId }) {
    // Get tab index from URL query parameter if present
    const getInitialTabFromUrl = () => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const tabParam = urlParams.get('tab');
            return tabParam ? parseInt(tabParam, 10) : 0;
        }
        return 0;
    };

    const [tabValue, setTabValue] = useState(getInitialTabFromUrl());
    const [teamTimeOffEvents, setTeamTimeOffEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);

        // Update URL with the new tab value without reloading the page
        const url = new URL(window.location);
        url.searchParams.set('tab', newValue);
        window.history.pushState({}, '', url);
    };

    const handleLogout = async () => {
        await signOut(auth);
        window.location.href = "/login";
    };

    const handleBack = () => {
        window.location.href = "/employee";
    };

    useEffect(() => {
        const fetchTeamMembers = async () => {
            try {
                const usersRef = collection(db, "organizations", orgId, "users");
                const usersSnap = await getDocs(usersRef);

                const members = usersSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setTeamMembers(members);
                return members;
            } catch (err) {
                console.error("Error fetching team members:", err);
                setError("Failed to load team members");
                return [];
            }
        };

        const fetchTeamTimeOff = async () => {
            setLoading(true);
            setError(null);

            try {
                // First fetch all team members to have their data
                const members = await fetchTeamMembers();

                // Fetch approved time off requests for all employees
                const timeOffRef = collection(db, "organizations", orgId, "timeOffRequests");
                const q = query(
                    timeOffRef,
                    where("status", "==", "approved")
                );

                const snapshot = await getDocs(q);

                // Create a lookup map for employee data
                const employeeMap = new Map();
                members.forEach(member => {
                    employeeMap.set(member.id, member);
                    if (member.email) {
                        employeeMap.set(member.email.toLowerCase(), member);
                    }
                });

                // Transform time off requests into FullCalendar events
                const events = snapshot.docs.map(doc => {
                    const data = doc.data();

                    // 1. Correctly handle the date conversion
                    let startDate, endDate;

                    // Handle Firestore timestamp objects
                    if (data.startDate && typeof data.startDate.toDate === 'function') {
                        startDate = data.startDate.toDate();
                    } else {
                        // Handle string dates or other date formats
                        startDate = new Date(data.startDate);
                    }

                    if (data.endDate && typeof data.endDate.toDate === 'function') {
                        endDate = data.endDate.toDate();
                    } else {
                        endDate = new Date(data.endDate);
                    }

                    // 2. Create a correct end date for FullCalendar
                    // For all-day events in FullCalendar, the end date is exclusive
                    // This means if the event ends on April 8, we need to set endDate to April 9
                    const adjustedEndDate = new Date(endDate);
                    adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);

                    // 3. Reset the time part to midnight to ensure proper all-day event display
                    startDate.setHours(0, 0, 0, 0);
                    adjustedEndDate.setHours(0, 0, 0, 0);

                    // 4. Determine employee name - with better fallbacks
                    let employeeName = "Unknown";

                    // Try using employeeName directly from the request
                    if (data.employeeName && data.employeeName !== "") {
                        employeeName = data.employeeName;
                    }
                    // If not found, try using userId to look up employee
                    else if (data.userId && employeeMap.has(data.userId)) {
                        const employee = employeeMap.get(data.userId);
                        employeeName = employee.name || employee.displayName || employee.email || "Unknown";
                    }
                    // If still not found, try using employeeEmail to look up
                    else if (data.employeeEmail && employeeMap.has(data.employeeEmail.toLowerCase())) {
                        const employee = employeeMap.get(data.employeeEmail.toLowerCase());
                        employeeName = employee.name || employee.displayName || employee.email || "Unknown";
                    }
                    // Last resort, try to extract name from the email
                    else if (data.employeeEmail) {
                        // Extract name part from email (before the @)
                        const emailParts = data.employeeEmail.split('@');
                        if (emailParts.length > 0) {
                            // Format it nicer by replacing dots with spaces and capitalizing
                            const namePart = emailParts[0]
                                .replace(/\./g, ' ')
                                .replace(/(\b\w)/g, match => match.toUpperCase());
                            employeeName = namePart;
                        }
                    }

                    return {
                        id: doc.id,
                        title: `${employeeName} - ${data.typeLabel || data.type}`,
                        start: startDate,
                        end: adjustedEndDate, // Use the adjusted end date
                        allDay: true,
                        backgroundColor: getTimeOffTypeColor(data.type),
                        borderColor: getTimeOffTypeColor(data.type),
                        extendedProps: {
                            employeeId: data.userId,
                            employeeName: employeeName,
                            email: data.employeeEmail || data.email,
                            requestType: data.type,
                            reason: data.reason
                        }
                    };
                });

                setTeamTimeOffEvents(events);
            } catch (err) {
                console.error("Error fetching team time off:", err);
                setError("Failed to load team calendar data");
            } finally {
                setLoading(false);
            }
        };

        fetchTeamTimeOff();
    }, [orgId]);

    const getTimeOffTypeColor = (type) => {
        switch (type?.toLowerCase()) {
            case 'vacation':
            case 'annual':
                return '#4CAF50'; // green
            case 'sick':
                return '#F44336'; // red
            case 'personal':
                return '#2196F3'; // blue
            case 'bereavement':
                return '#9C27B0'; // purple
            case 'jury duty':
            case 'jury':
                return '#FF9800'; // orange
            case 'maternity':
            case 'paternity':
            case 'parental':
                return '#E91E63'; // pink
            case 'military':
                return '#795548'; // brown
            case 'training':
                return '#009688'; // teal
            case 'unpaid':
                return '#607D8B'; // blue-grey
            default:
                return '#607D8B'; // blue-grey
        }
    };

    const handleEventClick = (clickInfo) => {
        const { event } = clickInfo;
        const { extendedProps } = event;

        // Format dates to be user-friendly
        const startDate = event.start.toLocaleDateString();
        // Subtract one day from end date as FullCalendar uses exclusive end dates
        const endDateObj = new Date(event.end);
        endDateObj.setDate(endDateObj.getDate() - 1);
        const endDate = endDateObj.toLocaleDateString();

        alert(
            `Time Off: ${extendedProps.employeeName} - ${extendedProps.requestType}\n` +
            `Dates: ${startDate} to ${endDate}\n` +
            (extendedProps.reason ? `Reason: ${extendedProps.reason}\n` : '') +
            `Employee: ${extendedProps.email || 'Unknown'}`
        );
    };

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Button
                        color="inherit"
                        onClick={handleBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{ mr: 2 }}
                    >
                        Back to Dashboard
                    </Button>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        TimeWarden
                    </Typography>
                    <IconButton
                        color="inherit"
                        onClick={() => window.location.href = "/employee/settings"}
                        sx={{ mr: 1 }}
                    >
                        <SettingsIcon />
                    </IconButton>
                    <Button color="inherit" onClick={handleLogout}>Logout</Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg">
                <Paper sx={{ mt: 5, p: 4, borderRadius: 2, boxShadow: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <CalendarTodayIcon fontSize="large" sx={{ mr: 2, color: 'primary.main' }} />
                        <Typography variant="h4" component="h1">
                            Team & Personal Dashboard
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 4 }} />

                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{ mb: 4 }}
                        variant="fullWidth"
                    >
                        <Tab
                            label="Team Absence Calendar"
                            icon={<GroupIcon />}
                            iconPosition="start"
                        />
                        <Tab
                            label="Attendance Metrics"
                            icon={<EventIcon />}
                            iconPosition="start"
                        />
                        <Tab
                            label="Time Tracking"
                            icon={<CalendarTodayIcon />}
                            iconPosition="start"
                        />
                    </Tabs>

                    <TabPanel value={tabValue} index={0}>
                        <Typography variant="h6" gutterBottom>
                            Team Absence Calendar
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            View when your colleagues are planning to be away.
                        </Typography>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : error ? (
                            <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
                        ) : (
                            <Box sx={{ height: 'calc(100vh - 300px)', minHeight: '600px' }}>
                                <FullCalendar
                                    plugins={[dayGridPlugin, interactionPlugin]}
                                    initialView="dayGridMonth"
                                    headerToolbar={{
                                        left: 'prev,next today',
                                        center: 'title',
                                        right: 'dayGridMonth,dayGridWeek'
                                    }}
                                    events={teamTimeOffEvents}
                                    eventClick={handleEventClick}
                                    height="100%"
                                />
                            </Box>
                        )}
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <AttendanceMetrics
                            userId={user.uid}
                            orgId={orgId}
                        />
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <TimeTrackingDashboard
                            userId={user.uid}
                            orgId={orgId}
                        />
                    </TabPanel>
                </Paper>
            </Container>
        </>
    );
}

export default TeamCalendarPage;